import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useEmployee } from '~/data/employee';
import { useAllEmployees } from '~/data/employees';
import {
  getCountryByLegalEntity,
  useAllLegalEntities,
} from '~/data/legalEntity';
import { BillableHoursProposalProps } from '~/data/proposals';
import { BillableHoursTypeConfig } from '~/data/rewarding/types';
import { useGetBillableHoursData } from '~/data/rewarding/useGetBillableHours';
import { useSelectedEmployeeId } from '~/hooks/employee/useSelectedEmployeeId';
import {
  ProposalFormPropsMessage,
  ProposalFormPropsMessageContext,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposals/components/Common';
import { EmployeeProposalsLayoutProps } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposals/components/EmployeeProposalsLayout/component';

export type BillableHoursProposalFormProps = {
  legalEntityCountryForUser: string;
  billableHoursTypeConfigs: BillableHoursTypeConfig[];
  messages: ProposalFormPropsMessage[];
};

type UseBillableHoursLayoutOutput = {
  newProposal?: BillableHoursProposalProps;
  formProps: BillableHoursProposalFormProps;
  isLoading: boolean;
};

export const useBillableHoursProposalsLayout = (
  props: EmployeeProposalsLayoutProps<BillableHoursProposalProps>,
): UseBillableHoursLayoutOutput => {
  const { newProposal } = props;
  const { t } = useTranslation();

  // Get EmployeeId
  const { selectedEmployeeId } = useSelectedEmployeeId();
  const employeeId = newProposal?.targetId || selectedEmployeeId;

  // Get EmploymentId
  const { allEmployees } = useAllEmployees();
  const employmentId = useMemo(
    () => allEmployees?.[employeeId]?.fEmpEmplId || -1,
    [allEmployees, employeeId],
  );

  const {
    data: billableHoursTypeConfigs,
    isLoading: isLoadingBillableHoursData,
  } = useGetBillableHoursData({ employeeId, employmentId });

  const billableHoursTypeConfigsAvailable = !!billableHoursTypeConfigs?.length;

  const newProposalEnabled = billableHoursTypeConfigsAvailable;

  // Resolve legalEntityCountryForUser
  const { employee, isLoading: isLoadingEmployee } = useEmployee();

  const {
    legalEntities,
    isLoading: isLoadingLegalEntities,
    hasData: legalEntityHasData,
  } = useAllLegalEntities();

  const legalEntityCountryForUser = useMemo(() => {
    const { empBasicData, empPosition, empPositionPending } = employee;

    const legalEntityId =
      ['NEW', 'PENDING'].includes(empBasicData.fEmpStatus) && empPositionPending
        ? empPositionPending.fEmpLegalEntity
        : empPosition.fEmpLegalEntity;

    return getCountryByLegalEntity(legalEntityId, legalEntities);
  }, [legalEntities, employee]);

  const messages: ProposalFormPropsMessage[] = [
    {
      show: !billableHoursTypeConfigsAvailable,
      payload: {
        context: ProposalFormPropsMessageContext.NewProposal,
        message: `${t('createNewProposalUnavailable')}: ${t(
          'noBillableHoursTypeConfigured',
        )}`,
      },
    },
  ]
    .filter((m) => m.show)
    .map((m) => m.payload);

  const formProps: BillableHoursProposalFormProps = {
    legalEntityCountryForUser,
    billableHoursTypeConfigs,
    messages,
  };

  const isLoading =
    isLoadingEmployee ||
    isLoadingBillableHoursData ||
    (isLoadingLegalEntities && !legalEntityHasData);

  return {
    newProposal: newProposalEnabled ? newProposal : undefined,
    formProps,
    isLoading,
  };
};
